import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { CheckCircle, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState("checking");
  const { refreshCart } = useCart();

  useEffect(() => {
    if (!orderId) {
      setStatus("failed");
      return;
    }
    api
      .get(`/orders/${orderId}`)
      .then((r) => {
        setOrder(r.data);
        const saved = r.data;
        const confirmed =
          saved.inventory_status === "sold" &&
          !saved.payment_review_required &&
          ["paid", "cod_pending"].includes(saved.payment_status) &&
          !["cancelled", "failed", "inventory_review"].includes(saved.status);
        setStatus(confirmed ? "paid" : "pending");
        refreshCart();
      })
      .catch(() => setStatus("failed"));
  }, [orderId, refreshCart]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full text-center bg-white border border-[#E7E5E4] rounded-lg p-10">
        {status === "checking" && (
          <>
            <Loader2 className="h-14 w-14 mx-auto text-[#7C1F30] animate-spin mb-6" />
            <h1 className="font-serif text-3xl mb-3">Confirming your order...</h1>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle className="h-14 w-14 mx-auto text-green-600 mb-6" />
            <h1 className="font-serif text-3xl mb-3">Thank you!</h1>
            <p className="text-sm text-[#78716C] mb-2">
              {order?.payment_status === "cod_pending"
                ? "Your order is confirmed. Pay on delivery."
                : "Your order has been placed successfully."}
            </p>
            {order && (
              <p className="text-xs text-[#78716C] mb-6">
                Order ID: <span className="font-mono">#{order.id.slice(0, 8)}</span>
              </p>
            )}
            <div className="flex flex-col gap-3">
              <Link
                to="/account?tab=orders"
                data-testid="view-orders-btn"
                className="bg-[#7C1F30] hover:bg-[#8D2A3D] text-white px-6 py-3 rounded-full uppercase text-xs tracking-[0.2em] transition-colors"
              >
                Track Your Order
              </Link>
              <Link to="/shop" className="text-sm text-[#78716C] hover:text-[#7C1F30]">
                Continue shopping
              </Link>
            </div>
          </>
        )}
        {status === "pending" && (
          <>
            <Loader2 className="h-14 w-14 mx-auto text-[#7C1F30] mb-6" />
            <h1 className="font-serif text-3xl mb-3">Order Received</h1>
            <p className="text-sm text-[#78716C] mb-6">
              {order?.payment_review_required
                ? "Your payment was received, but your order needs an inventory review. Please contact support. Do not pay again."
                : order?.payment_status === "paid"
                ? "Your payment was received. Order confirmation is pending. Do not pay again."
                : order?.payment_status === "cod_pending"
                ? "Your cash-on-delivery order is awaiting confirmation. Please check View Orders."
                : "Payment confirmation is pending. If you completed payment, do not pay again; check View Orders or contact support."}
            </p>
            <Link
              to="/account?tab=orders"
              className="inline-block bg-[#7C1F30] text-white px-6 py-3 rounded-full uppercase text-xs tracking-[0.2em]"
            >
              View Orders
            </Link>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle className="h-14 w-14 mx-auto text-[#7C1F30] mb-6" />
            <h1 className="font-serif text-3xl mb-3">Something went wrong</h1>
            <p className="text-sm text-[#78716C] mb-6">
              We couldn't confirm your order. Please contact support if you were charged.
            </p>
            <Link
              to="/cart"
              className="inline-block bg-[#7C1F30] text-white px-6 py-3 rounded-full uppercase text-xs tracking-[0.2em]"
            >
              Back to Cart
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
